export const PDF_DPI_SCALE = 300 / 72;
export const PDF_MAX_LONG_SIDE = 4096;
/** Upper bound for trimmed artwork: a small logo is vector, but past 600 DPI only the file grows. */
export const PDF_MAX_TRIM_SCALE = 600 / 72;
/** Long side of the low-resolution pass that finds the artwork's bounds. */
export const PDF_PREVIEW_LONG_SIDE = 768;

export function isPdfFile(file: Pick<File, 'name' | 'type'>): boolean {
  return file.type.toLowerCase() === 'application/pdf' || /\.pdf$/i.test(file.name);
}

/** Output file name for a PDF rendered to `extension` (`png` or `jpg`). */
export function imageNameForPdf(name: string, extension = 'png'): string {
  const trimmed = String(name || '').trim();
  if (!trimmed) return `document.${extension}`;
  return /\.pdf$/i.test(trimmed) ? trimmed.replace(/\.pdf$/i, `.${extension}`) : `${trimmed}.${extension}`;
}

export function pngNameForPdf(name: string): string {
  return imageNameForPdf(name, 'png');
}

export function pdfRenderScale(widthAtScaleOne: number, heightAtScaleOne: number): number {
  const longest = Math.max(Number(widthAtScaleOne) || 0, Number(heightAtScaleOne) || 0);
  if (longest <= 0) throw new Error('Invalid PDF page dimensions');
  return Math.min(PDF_DPI_SCALE, PDF_MAX_LONG_SIDE / longest);
}

/**
 * Scale for rendering trimmed artwork (size in PDF points): the long side gets the full pixel
 * budget, so a logo on an A4 page comes out far sharper than the whole page would, capped at
 * 600 DPI.
 */
export function trimRenderScale(widthPt: number, heightPt: number): number {
  const longest = Math.max(Number(widthPt) || 0, Number(heightPt) || 0);
  if (longest <= 0) throw new Error('Invalid PDF artwork dimensions');
  return Math.min(PDF_MAX_TRIM_SCALE, PDF_MAX_LONG_SIDE / longest);
}

export interface PixelBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Bounding box of the pixels with any opacity in RGBA data, grown by `margin` px (clamped to
 * the image). Null when every pixel is transparent.
 */
export function alphaBounds(data: ArrayLike<number>, width: number, height: number, margin = 1): PixelBounds | null {
  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < height; y++) {
    const row = y * width * 4;
    for (let x = 0; x < width; x++) {
      if (data[row + x * 4 + 3] === 0) continue;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
  }
  if (maxX < 0) return null;
  const x = Math.max(0, minX - margin);
  const y = Math.max(0, minY - margin);
  return {
    x,
    y,
    width: Math.min(width, maxX + 1 + margin) - x,
    height: Math.min(height, maxY + 1 + margin) - y,
  };
}

/** True when any pixel is not fully opaque. */
export function hasTransparency(data: ArrayLike<number>): boolean {
  for (let i = 3; i < data.length; i += 4) {
    if (data[i] !== 255) return true;
  }
  return false;
}

/** Canvas scale factor that should bring an encoded image of `size` bytes under `limit`. */
export function shrinkFactorForSize(size: number, limit: number): number {
  if (!(size > limit) || !(limit > 0)) return 1;
  // Encoded size grows roughly with pixel count (the square of the side); detailed artwork
  // compresses worse once smaller, so aim below the cap.
  return Math.max(0.1, Math.sqrt(limit / size) * 0.8);
}

/** Scale that fits a page (PDF points) into a `target` px long side, for picker thumbnails. */
export function thumbnailScale(widthPt: number, heightPt: number, target: number): number {
  const longest = Math.max(Number(widthPt) || 0, Number(heightPt) || 0);
  if (longest <= 0) return 1;
  return target / longest;
}

/** Next selected page (1-based) for an arrow / Home / End key in a grid of `columns`. */
export function movePageSelection(current: number, key: string, total: number, columns: number): number {
  const cols = Math.max(1, Math.floor(columns) || 1);
  const step: Record<string, number> = {
    ArrowLeft: -1,
    ArrowRight: 1,
    ArrowUp: -cols,
    ArrowDown: cols,
  };
  if (key === 'Home') return 1;
  if (key === 'End') return total;
  if (!(key in step)) return current;
  return Math.min(total, Math.max(1, current + step[key]));
}
